import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { listSessions } from "../api/endpoints";
import type { Paged, TrainingSession } from "../api/types";
import { ErrorMessage, Spinner } from "../components/ui";

/**
 * Ported from Views/Sessions/Index.cshtml ("History").
 *
 * The Razor page reads /api/activities, which merges kettlebell sessions with
 * Strava and Garmin imports. Those imports are LF-15/LF-17 in the feature
 * inventory and are dropped for the edge app, so this app has no
 * external_activities table and every row here is a kettlebell session.
 * The source filter is therefore omitted rather than rendered as a control
 * with a single option — note that in the C# app that filter is broken anyway:
 * ActivitiesController only accepts strava|manual|garmin and 400s on the
 * "kettlebell" value its own view sends.
 */

const PAGE_SIZE = 20;

const CARD =
  "bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-lg";
const PAGER_BUTTON =
  "px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-textDark-secondary bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-lg hover:bg-gray-50 dark:hover:bg-dark-elevated disabled:opacity-50";

function formatDate(value: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** The Razor card shows whole minutes; the API reports duration in seconds. */
function formatDuration(seconds: number | undefined): string | null {
  if (seconds === undefined || seconds <= 0) return null;
  return `${Math.round(seconds / 60)} min`;
}

function SessionCard({ session }: { session: TrainingSession }) {
  const names = session.exerciseNames ?? [];
  const duration = formatDuration(session.durationSeconds);
  const title = session.sessionName ?? `Session — ${session.exerciseCount ?? 0} exercises`;

  return (
    <div
      className={`${CARD} p-4 hover:shadow-sm transition-shadow`}
      data-testid="history-card-kettlebell"
    >
      <div className="flex items-start gap-3">
        <span className="text-2xl">
          <img src="/icons/lf/kettlebell.svg" className="w-6 h-6 opacity-60 dark:invert" alt="" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-sm font-medium text-gray-900 dark:text-textDark-primary truncate">
              {title}
            </h3>
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300">
              Kettlebell
            </span>
          </div>
          <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-textDark-muted">
            <span data-testid="history-card-date">{formatDate(session.completedAt)}</span>
            {duration && <span data-testid="history-card-duration">{duration}</span>}
            {session.totalSets !== undefined && (
              <span data-testid="history-card-sets">{session.totalSets} sets</span>
            )}
            {session.totalReps !== undefined && (
              <span data-testid="history-card-reps">{session.totalReps} reps</span>
            )}
          </div>
          {names.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1" data-testid="history-card-exercises">
              {names.slice(0, 4).map((name, i) => (
                <span
                  key={`${name}-${i}`}
                  className="inline-block px-2 py-0.5 bg-gray-100 dark:bg-dark-border text-xs text-gray-600 dark:text-textDark-muted rounded"
                >
                  {name}
                </span>
              ))}
              {names.length > 4 && (
                <span className="text-xs text-gray-400 dark:text-textDark-muted">
                  +{names.length - 4} more
                </span>
              )}
            </div>
          )}
        </div>
        <Link
          to={`/sessions/${session.id}`}
          className="text-sm text-gray-400 dark:text-textDark-muted hover:text-emerald-600 dark:hover:text-emerald-400 font-medium transition-colors"
          data-testid="history-view-detail"
        >
          View →
        </Link>
      </div>
    </div>
  );
}

function EmptyHistory() {
  return (
    <div className={`${CARD} shadow-sm text-center py-16 px-6`} data-testid="history-empty-state">
      <div className="w-16 h-16 rounded-2xl bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center mx-auto mb-4">
        <svg
          className="w-8 h-8 text-emerald-400 dark:text-emerald-600"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="14.25" r="9" />
          <path d="M10.5 20.061A6.015 6.015 0 0 1 6.436 16.5" />
          <path d="M7.5 6.455V2.25A1.5 1.5 0 0 1 9 0.75h6a1.5 1.5 0 0 1 1.5 1.5v4.205" />
        </svg>
      </div>
      <h2 className="text-heading-md text-gray-900 dark:text-textDark-primary mb-1.5">
        Your training log is empty
      </h2>
      <p className="text-sm text-gray-500 dark:text-textDark-muted max-w-xs mx-auto mb-6">
        Start your first session and track your progress here.
      </p>
      <Link
        to="/sessions/plan"
        className="rounded-lg font-medium px-5 py-2.5 text-sm bg-emerald-600 text-white hover:bg-emerald-700 transition-all active:scale-[0.98] shadow-sm"
        data-testid="history-plan-first-button"
      >
        Plan a Session
      </Link>
    </div>
  );
}

export default function SessionsPage() {
  const [page, setPage] = useState(1);
  const [data, setData] = useState<Paged<TrainingSession> | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (target: number) => {
    setLoading(true);
    setError(null);
    try {
      setData(await listSessions(PAGE_SIZE, (target - 1) * PAGE_SIZE));
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(page);
  }, [load, page]);

  const items = data?.items ?? [];
  const totalPages = data ? Math.max(Math.ceil(data.total / PAGE_SIZE), 1) : 1;

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="sessions-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-heading-xl text-gray-900 dark:text-textDark-primary">History</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-textDark-muted">
            Your kettlebell sessions.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/sessions/plan"
            className="rounded-lg font-medium px-4 py-2.5 min-h-[44px] text-sm bg-emerald-600 text-white hover:bg-emerald-700 transition-all active:scale-[0.98] inline-flex items-center gap-1.5 shadow-sm"
            data-testid="history-new-workout-button"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="2"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
            New Session
          </Link>
        </div>
      </div>

      <div aria-live="polite" aria-atomic="false">
        <div className="space-y-3" data-testid="history-list">
          {loading && <Spinner />}
          {error !== null && !loading && (
            <ErrorMessage error={error} onRetry={() => void load(page)} />
          )}
          {!loading && error === null && items.length === 0 && <EmptyHistory />}
          {!loading &&
            error === null &&
            items.map((session) => <SessionCard key={session.id} session={session} />)}
        </div>

        {totalPages > 1 && (
          <div
            className="flex items-center justify-center gap-4 mt-6"
            data-testid="history-pagination"
          >
            <button
              type="button"
              className={PAGER_BUTTON}
              data-testid="history-prev-btn"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(p - 1, 1))}
            >
              Previous
            </button>
            <span
              className="text-sm text-gray-500 dark:text-textDark-muted"
              data-testid="history-page-info"
            >
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              className={PAGER_BUTTON}
              data-testid="history-next-btn"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
